import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
} from '@mui/material';
import { Menu as MenuIcon, AccountCircle, Logout } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';
import { TextSizeControl } from './accessibility/TextSizeControl';

interface HeaderProps {
  title?: string;
  onMenuClick?: () => void;
  onProfileClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  title = 'Careva',
  onMenuClick,
  onProfileClick,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { state, dispatch } = useApp();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate('/profile');
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    authService.removeToken();
    dispatch({ type: 'LOGOUT' });
    navigate('/');
  };

  const isMenuOpen = Boolean(anchorEl);

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar>
        {onMenuClick && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box
          component="img"
          src="/logos/icon.png"
          alt="Careva"
          onClick={() => navigate('/dashboard')}
          sx={{
            height: 32,
            width: 32,
            cursor: 'pointer',
            mr: 2,
            '&:hover': { opacity: 0.8 }
          }}
        />
        <Typography 
          variant="h6" 
          component="div" 
          sx={{ 
            flexGrow: 1, 
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 }
          }}
          onClick={() => navigate('/dashboard')}
        >
          {title}
        </Typography>
        
        {/* User profile section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextSizeControl
            value={state.textSize}
            onChange={(size) => dispatch({ type: 'SET_TEXT_SIZE', payload: size })}
            invertColors
            compact
          />
          <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {state.user?.displayName || state.user?.email}
          </Typography>
          <IconButton
            color="inherit"
            aria-label="profile"
            onClick={handleProfileMenuOpen}
            aria-controls={isMenuOpen ? 'profile-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {state.user?.displayName?.charAt(0) || state.user?.email?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Box>

        {/* Profile menu */}
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleProfileClick}>
            <AccountCircle sx={{ mr: 1 }} />
            Profile
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <Logout sx={{ mr: 1 }} />
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
